import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Home, 
  User, 
  Briefcase, 
  DollarSign, 
  Video, 
  MessageSquare, 
  Newspaper, 
  Mail, 
  LogOut,
  Menu,
  X,
  Image,
  Music,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/hero', label: 'Hero Section', icon: Home },
  { path: '/admin/about', label: 'About', icon: User },
  { path: '/admin/gallery', label: 'Gallery', icon: Image },
  { path: '/admin/services', label: 'Services', icon: Briefcase },
  { path: '/admin/pricing', label: 'Pricing', icon: DollarSign },
  { path: '/admin/videos', label: 'Videos', icon: Video },
  { path: '/admin/testimonials', label: 'Testimonials', icon: MessageSquare },
  { path: '/admin/news', label: 'News', icon: Newspaper },
  { path: '/admin/contacts', label: 'Contact Forms', icon: Mail },
  { path: '/admin/seo', label: 'SEO Settings', icon: Search },
];

export const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    axios.get(`${API}/admin/verify`, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    });
  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {}
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop */}
      <aside className={`hidden md:flex flex-col bg-[#1A1A1A] text-white transition-all duration-300 ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
              <Music className="w-5 h-5" />
            </div>
            {isSidebarOpen && (
              <div>
                <h1 className="font-serif text-lg">The Sing Studio</h1>
                <p className="text-xs text-white/60">Admin Panel</p>
              </div>
            )}
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-accent text-white' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {isSidebarOpen && <span className="text-sm">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {isSidebarOpen && <span className="text-sm">Logout</span>}
          </button>
          <a
            href="/"
            target="_blank"
            className="flex items-center gap-3 px-3 py-2.5 mt-1 w-full text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <Home className="w-5 h-5" />
            {isSidebarOpen && <span className="text-sm">View Website</span>}
          </a>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#1A1A1A] text-white z-50 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
            <Music className="w-4 h-4" />
          </div>
          <span className="font-serif">Admin</span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-[#1A1A1A] text-white z-40 pt-16 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-lg ${
                    isActive ? 'bg-accent' : 'hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-3 w-full text-white/70 hover:bg-white/10 rounded-lg"
            >
              <LogOut className="w-5 h-5" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:p-8 p-4 pt-20 md:pt-8 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
